import { useState } from 'react';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';

export interface HoldDialogProps {
  jobOrderNo: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * A case the dentist has not paid for does not leave the lab.
 *
 * The reason is a fixed list rather than free text, because Accounts and
 * Reception both put cases on hold and the archive is easier to read when the
 * four common reasons are spelled the same way every time. The optional note
 * carries the detail.
 *
 * Since the request of 5 October 2026 (third), section 2, this is the
 * confirmation of Hold and not a form in front of one: built on the shared
 * `ConfirmSheet`, a bottom sheet on a phone, with the question said in plain
 * words above the reasons. The question follows the reason chosen, and says
 * what the server does besides: any clock running on the case stops. It asks
 * whoever is there, as it always did, and its confirm locks on the first tap.
 */
export function HoldDialog({ jobOrderNo, busy, onCancel, onConfirm }: HoldDialogProps) {
  const [reason, setReason] = useState<string>(strings.hold.reasons[0]);
  const [note, setNote] = useState('');

  return (
    <ConfirmSheet
      title={`${strings.hold.title} ${jobOrderNo}`}
      statement={strings.confirm.hold(jobOrderNo, reason)}
      confirmLabel={strings.hold.confirm}
      cancelLabel={strings.hold.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={() => onConfirm(note.trim() === '' ? reason : `${reason} · ${note.trim()}`)}
      onCancel={onCancel}
      busy={busy}
    >
      <div className={modalStyles.field}>
        <span className={modalStyles.label}>{strings.hold.reason}</span>
        <div className={modalStyles.choices} role="group" aria-label={strings.hold.reason}>
          {strings.hold.reasons.map((option) => (
            <button
              key={option}
              type="button"
              className={reason === option ? modalStyles.on : undefined}
              aria-pressed={reason === option}
              onClick={() => setReason(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="hold-note">
          {strings.hold.noteLabel}
        </label>
        <input
          id="hold-note"
          className={modalStyles.input}
          value={note}
          placeholder={strings.hold.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
        <span className={modalStyles.hint}>{strings.hold.lead}</span>
      </div>
    </ConfirmSheet>
  );
}
